import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  ClockIcon,
  EyeIcon,
  FunnelIcon,
  PencilIcon,
  PlusIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import { HseGuideButton, HseModuleGuide } from "../../../../components/dashboard/hse/HseModuleGuide";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { exportHseTiltakPdf, fetchHseTiltak } from "../../../../api/hse/tiltak";
import { HseRowActionButton, HseRowActionLink, HseRowActions } from "../../../../components/dashboard/hse/HseRowActions";
import { fetchUsers } from "../../../../api/users";
import { HSE_PRINT_STYLES, runWindowPrint } from "../../../../components/dashboard/hse/hsePrintStyles";
import {
  HSE_TILTAK_STATUSES,
  HSE_TILTAK_TYPES,
  filterTiltakForPrint,
  isTiltakOverdue,
  tiltakFromRecord,
} from "../../../../components/dashboard/hse/hseTiltakUtils";

const statusClass = (status, overdue) => {
  if (overdue) return "bg-rose-50 text-rose-800 ring-rose-200";
  if (status === "completed") return "bg-emerald-50 text-emerald-800 ring-emerald-200";
  if (status === "in_progress" || status === "started") return "bg-amber-50 text-amber-800 ring-amber-200";
  if (status === "cancelled") return "bg-slate-100 text-slate-600 ring-slate-200";
  return "bg-blue-50 text-blue-800 ring-blue-200";
};

export const HseKsTiltakList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [guideOpen, setGuideOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    query: "",
    tiltak_statuses: [],
    action_types: [],
    users: [],
  });
  const [userOptions, setUserOptions] = useState([]);
  const [exportingId, setExportingId] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState("open");
  const [printRows, setPrintRows] = useState([]);
  const pendingPrintRef = useRef(false);

  const eventId = searchParams.get("hse_event_id") || "";
  const evaluationId =
    searchParams.get("hse_evaluation_id") || searchParams.get("evaluation_id") || "";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await fetchHseTiltak({
        search: filters.query || undefined,
        status: filters.tiltak_statuses?.[0] || undefined,
        action_type: filters.action_types?.[0] || undefined,
        responsible_user_id: filters.users?.[0] || undefined,
        hse_event_id: eventId || undefined,
        hse_evaluation_id: evaluationId || undefined,
        page: 1,
        per_page: 100,
      });
      setRows((response?.data || []).map(tiltakFromRecord));
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filters, eventId, evaluationId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchUsers({
      filters: [{ name: "EMPLOYEE", value: true }],
      page: 1,
      per_page: 100,
      query: "",
      sort: "asc",
      sortBy: "name",
    })
      .then((res) => {
        if (cancelled) return;
        setUserOptions(
          (res?.data || []).map((row) => ({
            value: String(row.id),
            label: row.displayTitle || row.name || row.email || String(row.id),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setUserOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExportPdf = async (row, event) => {
    event?.stopPropagation();
    if (!row?.id || exportingId) return;
    try {
      setExportingId(row.id);
      await exportHseTiltakPdf(row.id, `hse-tiltak-${row.id}.pdf`);
    } catch (err) {
      toast.error(err.message || t("hse.tiltak.pdf_failed"));
    } finally {
      setExportingId(null);
    }
  };

  const handlePrint = () => {
    const next = filterTiltakForPrint(rows, printScope);
    if (next.length === 0) {
      toast.error(t("hse.tiltak.print_empty"));
      return;
    }
    pendingPrintRef.current = true;
    setPrintRows(next);
  };

  useEffect(() => {
    if (!pendingPrintRef.current || printRows.length === 0) return;
    pendingPrintRef.current = false;
    setPrintOpen(false);
    const timer = setTimeout(() => runWindowPrint(), 80);
    return () => clearTimeout(timer);
  }, [printRows]);

  const scopeLabel = useMemo(() => {
    if (printScope === "overdue") return t("hse.tiltak.print_scope_overdue");
    if (printScope === "completed") return t("hse.tiltak.print_scope_completed");
    if (printScope === "all") return t("hse.tiltak.print_scope_all");
    return t("hse.tiltak.print_scope_open");
  }, [printScope, t]);

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.tiltak.title")}
      subtitle={t("hse.tiltak.subtitle")}
      listCount={rows.length}
      mainButton={{
        label: t("hse.tiltak.new"),
        icon: PlusIcon,
        path: eventId
          ? `/dashboard/hse-ks/tiltak/new?hse_event_id=${eventId}`
          : "/dashboard/hse-ks/tiltak/new",
      }}
      actions={[
        {
          label: t("hse.tiltak.print_action"),
          action: () => setPrintOpen(true),
          icon: PrinterIcon,
          hidden: loading || error,
          menu: false,
        },
      ]}
    >
      <HseModuleGuide topic="tiltak" open={guideOpen} onClose={() => setGuideOpen(false)} />
      <style>{HSE_PRINT_STYLES}</style>

      <div className="hse-print-root hidden print:block px-6 py-4 text-slate-900">
        <h1 className="text-xl font-bold">{t("hse.tiltak.print_title")}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {scopeLabel}
          {" · "}
          {t("hse.tiltak.print_generated_at", { date: new Date().toLocaleString() })}
        </p>
        <table className="mt-4 w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="border-b border-slate-300 py-1.5 text-left font-semibold">
                {t("hse.tiltak.col_title")}
              </th>
              <th className="border-b border-slate-300 py-1.5 text-left font-semibold">
                {t("hse.tiltak.col_assignee")}
              </th>
              <th className="border-b border-slate-300 py-1.5 text-left font-semibold">
                {t("hse.tiltak.col_deadline")}
              </th>
              <th className="border-b border-slate-300 py-1.5 text-left font-semibold">
                {t("hse.tiltak.col_status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {printRows.map((row) => (
              <tr key={row.id}>
                <td className="border-b border-slate-200 py-1.5">{row.title}</td>
                <td className="border-b border-slate-200 py-1.5">
                  {row.responsible_user?.displayTitle ||
                    row.responsible_user?.name ||
                    row.assignee?.name ||
                    "—"}
                </td>
                <td className="border-b border-slate-200 py-1.5">
                  {row.deadline ? new Date(row.deadline).toLocaleDateString() : "—"}
                </td>
                <td className="border-b border-slate-200 py-1.5">
                  {t(`hse.tiltak.status.${row.status || "planned"}`)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HsePageBody className="hse-no-print">
        <div className="mb-4 flex justify-end print:hidden">
          <HseGuideButton onClick={() => setGuideOpen(true)} />
        </div>
        <div className="mb-4">
          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("hse.tiltak.search_placeholder")}
            usersFilterLabel={t("hse.tiltak.field.assignee")}
            userOptions={userOptions}
            initialFilters={filters}
            onFiltersChange={(next) =>
              setFilters({
                query: next.query || "",
                tiltak_statuses: next.tiltak_statuses || [],
                action_types: next.action_types || [],
                users: next.users || [],
              })
            }
            customDropdownFilters={[
              {
                key: "tiltak_statuses",
                label: t("hse.tiltak.col_status"),
                options: HSE_TILTAK_STATUSES.map((value) => ({
                  value,
                  label: t(`hse.tiltak.status.${value}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
              {
                key: "action_types",
                label: t("hse.tiltak.field.action_type"),
                options: HSE_TILTAK_TYPES.map((value) => ({
                  value,
                  label: t(`hse.tiltak.type.${value}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
            ]}
          />
        </div>

        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError errors={[t("hse.tiltak.load_failed")]} />
        ) : rows.length === 0 ? (
          <ResourceEmpty message={t("hse.tiltak.empty")} />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t("hse.tiltak.col_title")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t("hse.tiltak.col_type")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t("hse.tiltak.col_assignee")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t("hse.tiltak.col_deadline")}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t("hse.tiltak.col_status")}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700">
                    {t("hse.tiltak.col_actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const overdue = isTiltakOverdue(row);
                  return (
                    <tr
                      key={row.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/dashboard/hse-ks/tiltak/${row.id}`)}
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">{row.title}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {t(`hse.tiltak.type.${row.action_type || "generic"}`)}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.responsible_user?.displayTitle ||
                          row.responsible_user?.name ||
                          row.assignee?.name ||
                          "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          {overdue ? <ClockIcon className="h-4 w-4 text-rose-500" /> : null}
                          {row.deadline ? new Date(row.deadline).toLocaleDateString() : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusClass(row.status, overdue)}`}
                        >
                          {overdue
                            ? t("hse.tiltak.status_overdue")
                            : t(`hse.tiltak.status.${row.status || "planned"}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <HseRowActions>
                          <HseRowActionLink to={`/dashboard/hse-ks/tiltak/${row.id}`} icon={EyeIcon}>
                            {t("hse.tiltak.open")}
                          </HseRowActionLink>
                          <HseRowActionLink to={`/dashboard/hse-ks/tiltak/${row.id}/edit`} icon={PencilIcon}>
                            {t("hse.tiltak.edit")}
                          </HseRowActionLink>
                          <HseRowActionButton
                            icon={ArrowDownTrayIcon}
                            disabled={exportingId === row.id}
                            onClick={(event) => handleExportPdf(row, event)}
                          >
                            {t("hse.tiltak.export_pdf")}
                          </HseRowActionButton>
                        </HseRowActions>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </HsePageBody>

      <PMDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        title="hse.tiltak.print_dialog_title"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t("hse.tiltak.print_dialog_intro")}</p>
          <label className="block text-sm font-medium text-slate-700">
            {t("hse.tiltak.print_scope")}
            <select
              value={printScope}
              onChange={(event) => setPrintScope(event.target.value)}
              className="mt-2 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="open">{t("hse.tiltak.print_scope_open")}</option>
              <option value="overdue">{t("hse.tiltak.print_scope_overdue")}</option>
              <option value="completed">{t("hse.tiltak.print_scope_completed")}</option>
              <option value="all">{t("hse.tiltak.print_scope_all")}</option>
            </select>
          </label>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setPrintOpen(false)}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700"
            >
              {t("hse.tiltak.print_cancel")}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
            >
              <FunnelIcon className="h-4 w-4" />
              {t("hse.tiltak.print_submit")}
            </button>
          </div>
        </div>
      </PMDialog>
    </HsePageChrome>
  );
};
